import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MobileAppLayout from "../../layouts/MobileAppLayout";
import { getMyAffiliate, type MyAffiliate } from "../../services/affiliate";

const TYPE_FALLBACK: Record<string, string> = {
  coach: "Antrenor",
  parent: "Părinte",
  athlete: "Sportiv",
  club: "Club",
};

export default function AppAffiliate() {
  const { t } = useTranslation();
  const [me, setMe] = useState<MyAffiliate | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const typeLabel = (type: string): string =>
    type in TYPE_FALLBACK ? t(`affiliate.type_${type}`, TYPE_FALLBACK[type]) : t("affiliate.linkLabel", "Link");

  useEffect(() => {
    (async () => {
      try {
        setMe(await getMyAffiliate());
      } catch (e: any) {
        setErr(e?.response?.data?.error || t("affiliate.loadError", "Nu am putut încărca datele de afiliere."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copy = (link: string, key: string) => {
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? null : k)), 1500);
    });
  };

  const share = (link: string) => {
    if ((navigator as any).share) (navigator as any).share({ title: "iGROWth", url: link }).catch(() => {});
  };

  return (
    <MobileAppLayout>
      <div className="p-4 space-y-4">
        <div>
          <h1 className="text-xl font-extrabold text-slate-800">{t("affiliate.title", "Invită & câștigă")}</h1>
          <p className="text-sm text-slate-500">{t("affiliate.subtitle", "Împarte link-ul tău. Când cei aduși activează un cont plătit, primești recompensa.")}</p>
        </div>

        {err && <div className="p-3 text-sm rounded-xl bg-rose-50 border border-rose-200 text-rose-700">{err}</div>}

        {loading ? (
          <div className="py-10 text-center text-slate-400">{t("affiliate.loading", "Se încarcă…")}</div>
        ) : !me?.personal && !me?.club ? (
          <div className="p-4 text-sm text-center rounded-2xl bg-slate-50 border border-slate-200 text-slate-500">
            {t("affiliate.noLink", "Momentan nu ai un link de afiliat disponibil.")}
          </div>
        ) : (
          <>
            {me?.personal && (
              <div className="p-4 bg-white border rounded-2xl border-slate-200 shadow-sm">
                <div className="text-xs font-semibold text-indigo-600">{typeLabel(me.personal.type)}</div>
                <div className="mt-1 mb-3 text-sm text-slate-500">{t("affiliate.code", "Cod:")} <span className="font-mono font-bold text-slate-700">{me.personal.code}</span></div>
                <div className="p-2.5 mb-3 text-xs break-all rounded-lg bg-slate-100 text-slate-600">{me.personal.link}</div>
                <div className="flex gap-2">
                  <button onClick={() => copy(me.personal!.link, "p")}
                    className="flex-1 py-2.5 text-sm font-semibold text-white rounded-xl bg-indigo-600">
                    {copied === "p" ? t("affiliate.copied", "Copiat ✓") : t("affiliate.copyLink", "Copiază link")}
                  </button>
                  {(navigator as any).share && (
                    <button onClick={() => share(me.personal!.link)}
                      className="px-4 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 text-slate-700">
                      {t("affiliate.send", "Trimite")}
                    </button>
                  )}
                </div>
              </div>
            )}

            {me && (
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-white border rounded-2xl border-slate-200">
                  <div className="text-xs text-slate-400">{t("affiliate.referrals", "Recomandări")}</div>
                  <div className="text-2xl font-extrabold text-slate-800">{me.referrals.total}</div>
                </div>
                <div className="p-4 bg-white border rounded-2xl border-slate-200">
                  <div className="text-xs text-slate-400">{t("affiliate.converted", "Convertite")}</div>
                  <div className="text-2xl font-extrabold text-emerald-600">{me.referrals.converted}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </MobileAppLayout>
  );
}
